import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/Common/BackButton';
import { setRedirectURL, setStudentLocationAuth } from '../../store/userSlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from '../../firebase';

interface LocationOption {
  id: LocationType;
  title: string;
  subtitle?: string;
  icon: string;
  className: string;
}

export enum LocationType {
  School = 'school',
  Home = 'home',
}

const LocationSelection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const studentLocationAuth = useSelector((state: RootState) => state.user.studentLocationAuth);
  const [animateOptions, setAnimateOptions] = useState(false);

  useEffect(() => {
    if (!!auth.currentUser) {
      dispatch(setRedirectURL('/'));
      return;
    }
    const timer = setTimeout(() => setAnimateOptions(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const locationOptions: LocationOption[] = [
    {
      id: LocationType.School,
      title: 'School',
      icon: '/school-location-logo.png',
      className: 'bg-[#edf1d0]',
    },
    {
      id: LocationType.Home,
      title: 'Home',
      subtitle: 'Or anywhere else not at school',
      icon: '/home-location-logo.png',
      className: 'bg-[#ffefcd]',
    },
  ];

  const handleOptionSelect = (optionId: LocationType) => {
    dispatch(setStudentLocationAuth(optionId));
    setTimeout(() => {
      navigate('/auth/student/login?location=' + optionId);
    }, 300);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-whiteGrayBackground font-sofiaSans flex flex-col relative">
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div
            className={`text-center mb-10 transition-all duration-700 ${animateOptions ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}
          >
            <h1 className="text-3xl font-bold text-black mb-4 leading-tight">Where are you?</h1>
            <p className="text-base text-left text-black leading-relaxed max-w-xs mx-auto">
              Before you continue, we need to know where you're using Build My Story.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-center items-center">
            {locationOptions.map((option, idx) => (
              <div key={option.id} className={`flex justify-center h-28`}>
                <div
                  className={`
                    max-w-[260px] w-full ${option.className} rounded-lg p-1 flex flex-col justify-center items-center cursor-pointer transition-all duration-500
                    ${animateOptions ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                    ${
                      studentLocationAuth === option.id
                        ? 'border-secondaryStoryAdditionalLightBlueText scale-105 z-10'
                        : studentLocationAuth
                          ? 'opacity-50'
                          : 'hover:shadow-md hover:border-secondaryStoryAdditionalLightBlueText hover:scale-105'
                    }
                  `}
                  style={{ transitionDelay: animateOptions ? `${100 + idx * 200}ms` : '0ms' }}
                  onClick={() => handleOptionSelect(option.id)}
                >
                  <div className="text-5xl max-h-12">
                    <img className="w-12 h-12" src={option.icon} alt={option.id} />
                  </div>
                  <h2 className="text-xl font-bold text-secondaryStoryBlackText">{option.title}</h2>
                  {option.subtitle && (
                    <p className="text-sm text-secondaryDescriptionTextColor text-center leading-relaxed">
                      {option.subtitle}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <BackButton onClick={() => window.history.back()}></BackButton>
        </div>
      </div>
    </div>
  );
};

export default LocationSelection;
